import { http } from '@/lib/http';
import { saveBlob } from '@/lib/reconciliation/api';
import type { DisruptionDossier, ReportExport, ReportFilters, ReportsOverview } from './types';

// operator-bff real reporting (#823):
//   GET /v1/reports/overview(.csv)?from&to       the window overview + one row per disruption
//   GET /v1/reports/daily.csv?from&to            one row per UTC day
//   GET /v1/reports/operators.csv?from&to        one row per operator across the window
//   GET /v1/reports/disruptions/{urn}/dossier(.csv)  the printable "ficha de disrupción"
// The `.csv` twins are Excel-ready (UTF-8 BOM, `;`, Spanish headers) and carry the
// filename in `Content-Disposition`, like the reconciliation export.

export interface CsvFile {
  blob: Blob;
  filename: string;
}

function buildQs(filters: ReportFilters): string {
  const sp = new URLSearchParams();
  if (filters.from) sp.set('from', filters.from);
  if (filters.to) sp.set('to', filters.to);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

async function fetchCsv(path: string, fallback: string): Promise<CsvFile> {
  const res = await http.get(path);
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? fallback };
}

export async function fetchReportsOverview(filters: ReportFilters): Promise<ReportsOverview> {
  return http.get(`v1/reports/overview${buildQs(filters)}`).json<ReportsOverview>();
}

export function fetchReportCsv(kind: ReportExport, filters: ReportFilters): Promise<CsvFile> {
  return fetchCsv(`v1/reports/${kind}.csv${buildQs(filters)}`, `reporte-${kind}.csv`);
}

const dossierPath = (urn: string) => `v1/reports/disruptions/${encodeURIComponent(urn)}/dossier`;

export async function fetchDossier(urn: string): Promise<DisruptionDossier> {
  return http.get(dossierPath(urn)).json<DisruptionDossier>();
}

export function fetchDossierCsv(urn: string): Promise<CsvFile> {
  return fetchCsv(`${dossierPath(urn)}.csv`, 'ficha.csv');
}

export { saveBlob };
